import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { useAppStatus } from "./lib/app-status";
import { AnnouncementBanner } from "./components/settings/AnnouncementBanner";
import { JoinGate } from "./components/JoinGate";
import { Splash } from "./components/Splash";
import { Home } from "./components/home/Home";
import { RoomPage } from "./components/home/RoomPage";
import { useAuth } from "./auth/auth";
import { activeCall } from "./room/active-call";
import { ActiveCallBar } from "./components/call/ActiveCallBar";
import { RoomSwitchGuard } from "./components/call/RoomSwitchDialog";
import { useAwayCall, useReportFailedAwayCall } from "./components/call/use-away-call";
import { cn } from "./lib/utils";

// eslint-disable-next-line react-refresh/only-export-components -- The router owns this private root component and exports a router singleton; edits reload the route tree.
function Root() {
  const { user, loading, signOut } = useAuth();
  const status = useAppStatus(true);
  const awayCall = useAwayCall();
  useReportFailedAwayCall();
  // The call lives in the module-level owner, above every route; only signing
  // out ends it from here.
  const signedIn = Boolean(user);
  useEffect(() => {
    if (!signedIn) activeCall.endCall();
  }, [signedIn]);
  if (loading) return <Splash />;
  return (
    <>
      <JoinGate />
      {user ? (
        <div className="flex h-dvh min-w-0 flex-col bg-background text-foreground">
          {status.data ? <AnnouncementBanner announcement={status.data.announcement} /> : null}
          {/* Reserve the floating two-row call dock's height so it covers no content. */}
          <div className={cn("min-h-0 flex-1", awayCall && "pb-32")}>
            <Outlet />
          </div>
          {awayCall ? <ActiveCallBar call={awayCall} user={user} onSignOut={signOut} /> : null}
          <RoomSwitchGuard />
        </div>
      ) : null}
    </>
  );
}

const rootRoute = createRootRoute({
  component: Root,
  notFoundComponent: () => <Navigate to="/" replace />,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  // The room controller sends a replaced connection to /?moved=1, and a call
  // that failed while away to /?ended=<code>.
  validateSearch: (search: Record<string, unknown>): { moved?: true; ended?: string } => ({
    ...("moved" in search ? { moved: true as const } : {}),
    ...(typeof search.ended === "string" && search.ended ? { ended: search.ended } : {}),
  }),
  component: Home,
});

const roomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/room/$roomId",
  component: RoomPage,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([homeRoute, roomRoute]),
});

// A left or replaced call returns home without reloading the document.
activeCall.bindNavigation((moved) => {
  void router.navigate({ to: "/", search: moved ? { moved: true } : {}, replace: moved });
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
